import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, MessageSquare, FolderOpen, ExternalLink, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Projects = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    repository_url: '',
    deployment_path: ''
  });

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading projects:', error);
      toast.error('Ошибка загрузки проектов');
      setLoading(false);
    }
  };

  const createProject = async () => {
    try {
      const response = await axios.post(`${API}/projects/create`, null, {
        params: {
          project_name: formData.name,
          description: formData.description
        }
      });
      
      if (response.data.success) {
        toast.success('Проект создан');
        setOpen(false);
        loadProjects();
        setFormData({ name: '', description: '', repository_url: '', deployment_path: '', llm_provider: 'openai', llm_model: 'gpt-5' });
      }
    } catch (error) {
      console.error('Error creating project:', error);
      toast.error('Ошибка создания проекта');
    }
  };

  const deleteProject = async (id) => {
    if (!window.confirm('Удалить проект?')) return;
    
    try {
      await axios.delete(`${API}/projects/${id}`);
      toast.success('Проект удален');
      loadProjects();
    } catch (error) {
      console.error('Error deleting project:', error);
      toast.error('Ошибка удаления проекта');
    }
  };

  const openProjectChat = (projectId) => {
    // Переходим в чат с project_id
    navigate(`/chat?project=${projectId}`);
  };

  const openWorkspace = (projectId) => {
    navigate(`/project/${projectId}`);
  };

  return (
    <div className="h-full overflow-auto p-8" data-testid="projects-page">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-bold text-white mb-2">Проекты</h1>
          <p className="text-gray-400">Управление вашими проектами</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-to-r from-violet-600 to-fuchsia-600">
              <Plus className="w-4 h-4 mr-2" />
              Создать проект
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-gray-900 border-white/20 text-white">
            <DialogHeader>
              <DialogTitle>Новый проект</DialogTitle>
              <DialogDescription className="text-gray-400">
                Создайте новый проект или используйте AI Chat для автоматического создания
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Название</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="bg-white/5 border-white/20 text-white"
                />
              </div>
              <div>
                <Label>Описание</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="bg-white/5 border-white/20 text-white"
                />
              </div>
              
              <div>
                <Label className="text-gray-300">LLM Provider</Label>
                <Select 
                  value={formData.llm_provider} 
                  onValueChange={(value) => setFormData({...formData, llm_provider: value})}
                >
                  <SelectTrigger className="bg-white/5 border-white/20 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-900 border-white/20">
                    <SelectItem value="openai" className="text-white">OpenAI</SelectItem>
                    <SelectItem value="anthropic" className="text-white">Anthropic (Claude)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-gray-300">LLM Model</Label>
                <Select 
                  value={formData.llm_model} 
                  onValueChange={(value) => setFormData({...formData, llm_model: value})}
                >
                  <SelectTrigger className="bg-white/5 border-white/20 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-900 border-white/20">
                    {formData.llm_provider === 'openai' ? (
                      <>
                        <SelectItem value="gpt-5" className="text-white">GPT-5 (Latest)</SelectItem>
                        <SelectItem value="gpt-4o" className="text-white">GPT-4o</SelectItem>
                        <SelectItem value="gpt-4-turbo" className="text-white">GPT-4 Turbo</SelectItem>
                      </>
                    ) : (
                      <>
                        <SelectItem value="claude-sonnet-4-20250514" className="text-white">Claude Sonnet 4</SelectItem>
                        <SelectItem value="claude-4-5-sonnet-20250219" className="text-white">Claude 4.5 Sonnet</SelectItem>
                        <SelectItem value="claude-3-7-sonnet-20250219" className="text-white">Claude 3.7 Sonnet</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={createProject} className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600">
                Создать
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-12 h-12 animate-spin text-violet-500" />
        </div>
      ) : projects.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 text-center">
          <FolderOpen className="w-16 h-16 text-gray-600 mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">Нет проектов</h3>
          <p className="text-gray-400 mb-4">Создайте первый проект через AI Chat или вручную</p>
          <Button onClick={() => navigate('/chat')} className="bg-gradient-to-r from-violet-600 to-fuchsia-600">
            Перейти в AI Chat
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((project) => (
            <Card key={project.id} className="glass-panel border-white/10 bg-white/5 hover:bg-white/10 transition-all">
              <CardHeader>
                <CardTitle className="text-white flex items-center justify-between">
                  <span>{project.name}</span>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    project.status === 'ready' ? 'bg-green-500/20 text-green-300' :
                    project.status === 'building' ? 'bg-yellow-500/20 text-yellow-300' :
                    'bg-blue-500/20 text-blue-300'
                  }`}>
                    {project.status}
                  </span>
                </CardTitle>
                <CardDescription className="text-gray-400">{project.description || 'Нет описания'}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2">
                  <Button
                    onClick={() => openProjectChat(project.id)}
                    variant="outline"
                    className="flex-1 border-violet-500/30 hover:bg-violet-500/20 text-white"
                  >
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Chat
                  </Button>
                  <Button
                    onClick={() => openWorkspace(project.id)}
                    variant="outline"
                    className="flex-1 border-blue-500/30 hover:bg-blue-500/20 text-white"
                  >
                    <FolderOpen className="w-4 h-4 mr-2" />
                    Workspace
                  </Button>
                  <Button
                    onClick={() => deleteProject(project.id)}
                    variant="outline"
                    size="icon"
                    className="border-red-500/30 hover:bg-red-500/20 text-red-400"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Projects;